import { useCallback, useEffect, useRef } from 'react';

import { DEBUG } from '../lib/debug';
import { writeDebugBundle } from '../lib/debug-folder';
import { rotateBlob90Clockwise } from '../lib/image-rotate';
import type { ManualQuad, WorkerRequest, WorkerResponse } from '../lib/types';
import { uuid } from '../lib/uuid';
import { useAppStore } from '../store';

interface QueueItem {
  id: string;
  file: File;
  lang: string;
  /** Dispatch generation for this session — see generationByIdRef. */
  generation: number;
  manualQuad?: ManualQuad;
  /** Stop after scan-correct and park for crop review (single uploads). */
  correctOnly?: boolean;
  /** Input is the review-approved JPEG — worker skips auto-detection. */
  skipCorrect?: boolean;
}

/** The run (session + dispatch generation) a busy worker is executing. */
interface InFlightRun {
  id: string;
  generation: number;
}

export interface ReceiptProcessor {
  /** Enqueue one or more files for processing across the worker pool. */
  processFiles: (files: File[], lang: string) => void;
  /** Cancel everything in flight + queued and tear down the pool. */
  cancel: () => void;
  /**
   * DEBUG-only: set the directory the hook auto-saves debug bundles to.
   * Pass null to disable. No-op when DEBUG is false.
   */
  setDebugFolder: (handle: FileSystemDirectoryHandle | null) => void;
  /**
   * Reprocess an existing session with caller-supplied corner positions
   * instead of running auto scan-correct. Used by the manual-crop modal
   * after the user adjusts the four-corner overlay. Looks up the
   * session's stored originalFile and re-dispatches it through the worker
   * pool, reusing the same session id so the UI updates in place.
   */
  reprocessWithManualQuad: (id: string, manualQuad: ManualQuad, lang: string) => void;
  /**
   * Approve a session parked in the crop-review gate: dispatch the
   * previewed corrected image through preprocess + OCR + parse. No-op
   * unless the session is in the 'review' state.
   */
  confirmScan: (id: string, lang: string) => void;
  /**
   * Rotate a session's corrected image 90° clockwise client-side and
   * re-dispatch it through the standard pipeline so OCR runs on the
   * right orientation. The rotated File becomes the session's new
   * originalFile so further rotates / recrops work from the rotated
   * image (each click is +90° from the current orientation). Returns
   * a promise that resolves once dispatch starts (not when processing
   * completes); the UI watches the session state for that.
   */
  reprocessWithRotation: (id: string, lang: string) => Promise<void>;
  /**
   * Re-run one session's OCR under a different language, after the user
   * corrects the language on its card. Re-dispatches the session's
   * originalFile through the pool under the same session id so the card
   * updates in place.
   */
  reprocessWithLang: (id: string, lang: string) => void;
}

/**
 * How many OCR workers to run in parallel. Adapts to the client's
 * hardware so we get aggressive throughput on desktop without OOMing
 * mobile.
 *
 * Two regimes:
 *   - **Resource-constrained** (mobile devices, or Chromium browsers
 *     reporting ≤2 GB device memory): cap the pool at 2. A mobile
 *     Safari tab dies past ~1 GB total, and that budget has to cover
 *     the whole React tree + image buffers + scan-correct WebGL
 *     textures + Tesseract instances — only some of which are this
 *     pool. Two workers is enough to overlap one OCR pass with
 *     another's preprocessing without crowding the budget.
 *   - **Desktop / high-RAM**: spawn one worker per logical core
 *     minus 2, leaving headroom for the main thread, the OS, and the
 *     user's other tabs. So a 16-thread desktop runs 14 workers, an
 *     8-thread laptop 6, a 4-thread machine 2.
 *
 * Tesseract.js v5 also bursts up to ~4 SAB threads INTERNALLY per
 * worker (when nginx COOP/COEP lights up cross-origin isolation), so
 * the actual thread count is ~4N — the OS scheduler time-shares them
 * across the cores we left available.
 *
 * Each Tesseract instance holds ~80 MB. A 16-thread desktop pool of
 * 14 ≈ 1.1 GB, fine on 8+ GB RAM. Mobile worst case is 2 workers ≈
 * 160 MB Tesseract — well under the tab budget.
 *
 * Falls back to 1 worker when navigator.hardwareConcurrency is missing
 * (older Safari, some embedded browsers).
 */
function computePoolSize(): number {
  const hw = navigator.hardwareConcurrency ?? 2;
  if (isResourceConstrained()) {
    return Math.min(2, Math.max(1, Math.floor(hw / 2)));
  }
  return Math.max(1, hw - 2);
}

function isResourceConstrained(): boolean {
  return isMobileDevice() || isLowMemoryDevice();
}

/**
 * Strongest signal: Chromium's UA-CH `userAgentData.mobile`. Falls back
 * to a UA-string regex for Safari / Firefox where UA-CH isn't shipped.
 * False negatives on edge browsers are acceptable — they fall through
 * to the desktop branch and we'd just over-spawn workers a bit.
 */
function isMobileDevice(): boolean {
  const uaData = (
    navigator as { userAgentData?: { mobile?: boolean } }
  ).userAgentData;
  if (uaData?.mobile === true) return true;
  if (uaData?.mobile === false) return false;
  if (typeof navigator.userAgent === 'string') {
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  }
  return false;
}

/**
 * `navigator.deviceMemory` is Chromium-only and reports RAM in GB,
 * rounded down + capped at 8 for fingerprinting resistance. ≤2 GB is
 * a real signal that we should stay small; absent or ≥4 means we
 * trust the desktop / hardwareConcurrency path.
 */
function isLowMemoryDevice(): boolean {
  const dm = (navigator as { deviceMemory?: number }).deviceMemory;
  return typeof dm === 'number' && dm <= 2;
}

/**
 * Vite recognizes this exact `new Worker(new URL(...))` pattern and
 * bundles the worker as a separate chunk. Don't refactor without
 * checking Vite's worker docs.
 */
function spawnWorker(): Worker {
  return new Worker(new URL('../workers/ocr.worker.ts', import.meta.url), {
    type: 'module',
  });
}

/**
 * Owns a pool of OCR Web Workers and a shared per-file queue.
 *
 * Each enqueued file is dispatched to whichever worker is idle. Each
 * worker keeps its own persistent Tesseract instance (initialised
 * lazily on first request, reused for the session — see
 * `pipeline/ocr.ts`). For batch drops on multi-core machines this is
 * roughly an N× speed-up vs the single-worker design that came before.
 *
 * StrictMode safety: the cleanup in the unmount effect terminates every
 * worker. The queue + in-flight maps live in refs so they survive the
 * StrictMode double-invoke without spawning duplicate workers.
 */
export function useReceiptProcessor(): ReceiptProcessor {
  const workersRef = useRef<Worker[]>([]);
  /** Indices into workersRef of currently-idle workers. Acts as a stack. */
  const idleWorkerIdxRef = useRef<number[]>([]);
  /** workerIndex → the run currently executing on that worker. */
  const inFlightByWorkerRef = useRef<Map<number, InFlightRun>>(new Map());
  /**
   * sessionId → latest dispatch generation. Bumped on every (re)dispatch
   * and stamped into the queue item. A worker message whose run carries
   * an older generation is stale — a re-dispatch (recrop / rotate /
   * language change) superseded it while it was still running on a
   * worker — and must not touch the session: a stale result would
   * clobber the fresh one, and a stale stage message would flip a
   * finished card back to processing. Stale terminal messages still
   * free the worker slot.
   */
  const generationByIdRef = useRef<Map<string, number>>(new Map());
  const queueRef = useRef<QueueItem[]>([]);
  /**
   * Session ids with a rotate currently awaiting its canvas pass.
   * Guards against a double-click dispatching two rotations of the
   * same source image (+90° twice instead of +180°).
   */
  const rotatingRef = useRef<Set<string>>(new Set());
  // DEBUG-only: original File handles, kept until the corresponding result
  // arrives so we can include the original in the auto-saved debug bundle.
  // Cleared after each save (or on error) to avoid retaining file blobs.
  const inFlightFilesRef = useRef<Map<string, File>>(new Map());
  const debugFolderRef = useRef<FileSystemDirectoryHandle | null>(null);

  const pump = useCallback((): void => {
    // Drain as many queued items as there are idle workers.
    while (queueRef.current.length > 0 && idleWorkerIdxRef.current.length > 0) {
      const next = queueRef.current.shift()!;
      const workerIdx = idleWorkerIdxRef.current.shift()!;
      const worker = workersRef.current[workerIdx];
      if (worker === undefined) {
        // Defensive only — not reachable today: cancel/unmount clear the
        // idle list together with workersRef, indices are never spliced
        // out, and a crashed worker is replaced in place at the same
        // index before its slot returns to idle. Kept so a future pool
        // change can't silently post to a missing worker.
        queueRef.current.unshift(next);
        return;
      }
      inFlightByWorkerRef.current.set(workerIdx, {
        id: next.id,
        generation: next.generation,
      });
      if (DEBUG) inFlightFilesRef.current.set(next.id, next.file);
      const request: WorkerRequest = {
        kind: 'process',
        id: next.id,
        file: next.file,
        lang: next.lang,
      };
      if (next.manualQuad !== undefined) request.manualQuad = next.manualQuad;
      if (next.correctOnly === true) request.correctOnly = true;
      if (next.skipCorrect === true) request.skipCorrect = true;
      worker.postMessage(request);
    }
  }, []);

  const freeWorkerSlot = useCallback(
    (idx: number): void => {
      inFlightByWorkerRef.current.delete(idx);
      idleWorkerIdxRef.current.push(idx);
      pump();
    },
    [pump],
  );

  const attachListeners = useCallback(
    // Named function expression so the crash handler can re-attach
    // listeners to the replacement worker it spawns.
    function attach(worker: Worker, idx: number): void {
      worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
        const msg = event.data;
        const store = useAppStore.getState();
        const run = inFlightByWorkerRef.current.get(idx);
        // "Live" = this message belongs to the run currently assigned to
        // this worker AND that run is still the session's latest
        // dispatch (see generationByIdRef). Anything else is stale and
        // must not touch the session.
        const live =
          run !== undefined &&
          run.id === msg.id &&
          run.generation === generationByIdRef.current.get(msg.id);
        switch (msg.kind) {
          case 'stage':
            if (live) store.setStage(msg.id, msg.stage, msg.progress, msg.message);
            break;
          case 'result':
            if (live) {
              store.setResult(msg.id, msg.receipt, msg.ocr, msg.findings);
              if (DEBUG && debugFolderRef.current !== null) {
                const folder = debugFolderRef.current;
                const file = inFlightFilesRef.current.get(msg.id);
                if (file !== undefined) {
                  // Read the just-updated session for the corrected image and
                  // debug log (both written by store mutations earlier in the
                  // same message-loop turn).
                  const session = useAppStore
                    .getState()
                    .sessions.find((s) => s.id === msg.id);
                  if (session !== undefined) {
                    void writeDebugBundle(folder, {
                      filename: session.filename,
                      originalFile: file,
                      correctedImage: session.correctedImage,
                      debugLog: session.debugLog,
                      ocr: session.ocrDebug,
                      receipt: session.receipt,
                    });
                  }
                }
              }
              if (DEBUG) inFlightFilesRef.current.delete(msg.id);
            }
            // Free the slot even for a stale result — the worker really
            // did finish its run.
            if (run !== undefined && run.id === msg.id) freeWorkerSlot(idx);
            break;
          case 'error':
            if (live) {
              store.setError(msg.id, msg.error, msg.stage);
              if (DEBUG) inFlightFilesRef.current.delete(msg.id);
            }
            if (run !== undefined && run.id === msg.id) freeWorkerSlot(idx);
            break;
          case 'debug':
            if (live) store.appendDebug(msg.id, msg.entry);
            break;
          case 'corrected-image':
            if (live) {
              store.setCorrectedImage(
                msg.id,
                {
                  blob: msg.blob,
                  width: msg.width,
                  height: msg.height,
                  changed: msg.changed,
                },
                msg.quad,
              );
            }
            break;
          case 'awaiting-review':
            // Terminal message for a correctOnly run: park the session in
            // the crop-review gate. The worker is idle again — free the
            // slot exactly like result/error.
            if (live) store.setAwaitingReview(msg.id);
            if (run !== undefined && run.id === msg.id) freeWorkerSlot(idx);
            break;
        }
      });
      worker.addEventListener('error', (event: ErrorEvent) => {
        // Worker crashed. A crashed worker can never run another job, so
        // terminate it and install a fresh one AT THE SAME INDEX before
        // the slot goes back to idle — returning the dead worker's index
        // would make the next queued item hang forever.
        if (workersRef.current[idx] !== worker) return; // pool torn down / already replaced
        worker.terminate();
        const fresh = spawnWorker();
        workersRef.current[idx] = fresh;
        attach(fresh, idx);
        const run = inFlightByWorkerRef.current.get(idx);
        if (run === undefined) {
          // Idle crash (e.g. during module load): the index is already
          // in the idle list, which now points at the replacement.
          return;
        }
        if (run.generation === generationByIdRef.current.get(run.id)) {
          useAppStore.getState().setError(run.id, event.message || 'Worker crashed');
          if (DEBUG) inFlightFilesRef.current.delete(run.id);
        }
        freeWorkerSlot(idx);
      });
      worker.addEventListener('messageerror', () => {
        // A worker message failed to deserialize (realistically the
        // result payload). The run's output is lost but the worker
        // itself is fine — fail the session and return the slot to
        // idle so the pool doesn't silently shrink by one.
        if (workersRef.current[idx] !== worker) return;
        const run = inFlightByWorkerRef.current.get(idx);
        if (run === undefined) return;
        if (run.generation === generationByIdRef.current.get(run.id)) {
          useAppStore
            .getState()
            .setError(run.id, 'Worker response could not be read');
          if (DEBUG) inFlightFilesRef.current.delete(run.id);
        }
        freeWorkerSlot(idx);
      });
    },
    [freeWorkerSlot],
  );

  const ensureWorkerPool = useCallback((): void => {
    const target = computePoolSize();
    while (workersRef.current.length < target) {
      const worker = spawnWorker();
      const idx = workersRef.current.length;
      workersRef.current.push(worker);
      idleWorkerIdxRef.current.push(idx);
      attachListeners(worker, idx);
    }
  }, [attachListeners]);

  const processFiles = useCallback(
    (files: File[], lang: string): void => {
      if (files.length === 0) return;
      ensureWorkerPool();
      const store = useAppStore.getState();
      // Single-file drops (camera capture / one picked file) go through the
      // crop-review gate: the worker stops after scan-correct and the user
      // approves the crop before OCR runs. Batch drops skip the gate.
      const singleUpload = files.length === 1;
      for (const file of files) {
        const id = uuid();
        store.enqueue(id, file.name, file, lang, singleUpload);
        generationByIdRef.current.set(id, 1);
        const item: QueueItem = { id, file, lang, generation: 1 };
        if (singleUpload) item.correctOnly = true;
        queueRef.current.push(item);
      }
      pump();
    },
    [ensureWorkerPool, pump],
  );

  /**
   * Shared re-dispatch path for recrop / rotate / language change.
   * Drops any earlier queued entry for the session (the user may be
   * re-dispatching one that's already pending; we don't want both the
   * old and new requests to run), bumps the session's dispatch
   * generation (so messages from a superseded run still ON a worker are
   * recognised as stale and dropped), resets the session to queued, and
   * enqueues the file.
   */
  const redispatch = useCallback(
    (
      id: string,
      file: File,
      lang: string,
      opts?: Pick<QueueItem, 'manualQuad' | 'correctOnly' | 'skipCorrect'>,
    ): void => {
      queueRef.current = queueRef.current.filter((q) => q.id !== id);
      ensureWorkerPool();
      useAppStore.getState().resetForReprocess(id);
      const generation = (generationByIdRef.current.get(id) ?? 0) + 1;
      generationByIdRef.current.set(id, generation);
      const item: QueueItem = { id, file, lang, generation, ...opts };
      queueRef.current.push(item);
      pump();
    },
    [ensureWorkerPool, pump],
  );

  const reprocessWithManualQuad = useCallback(
    (id: string, manualQuad: ManualQuad, lang: string): void => {
      const session = useAppStore.getState().sessions.find((s) => s.id === id);
      if (session === undefined || session.originalFile === null) return;
      // Single-upload sessions land back in the crop-review gate so the
      // user sees the warped result of their corners before OCR runs —
      // the invariant is "OCR never runs on an unapproved crop". Batch
      // sessions keep the direct warp-then-scan behavior.
      redispatch(id, session.originalFile, lang, {
        manualQuad,
        ...(session.singleUpload === true ? { correctOnly: true } : {}),
      });
    },
    [redispatch],
  );

  /**
   * Crop-review gate approval: the user confirmed the previewed crop, so
   * run the rest of the pipeline (preprocess + OCR + parse) on exactly
   * that image. Dispatches the approved corrected JPEG with skipCorrect —
   * re-detecting on it could only disagree with what was approved. One
   * JPEG re-encode generation vs the original bitmap; same tradeoff the
   * rotate flow already makes.
   */
  const confirmScan = useCallback(
    (id: string, lang: string): void => {
      const session = useAppStore.getState().sessions.find((s) => s.id === id);
      if (session === undefined) return;
      // Only a parked session can be approved — also swallows a double-tap
      // (the first tap moves the session to queued).
      if (session.state.kind !== 'review') return;
      if (session.correctedImage === null) return;
      const approved = new File([session.correctedImage.blob], session.filename, {
        type: 'image/jpeg',
      });
      redispatch(id, approved, lang, { skipCorrect: true });
    },
    [redispatch],
  );

  /**
   * Re-run one already-scanned receipt under a different OCR language.
   * The user got, say, a German receipt parsed as English; changing the
   * language on the card has to re-OCR, because the language choice is
   * baked into the Tesseract model — there's nothing to re-derive from
   * the existing result.
   *
   * Feeds the ORIGINAL file, not the corrected JPEG: scan-correct is
   * language-independent, but re-warping from source keeps this path
   * identical to a first-time scan rather than compounding two
   * generations of JPEG artefacts onto the OCR input.
   */
  const reprocessWithLang = useCallback(
    (id: string, lang: string): void => {
      const session = useAppStore.getState().sessions.find((s) => s.id === id);
      if (session === undefined || session.originalFile === null) return;
      // Record the language before resetting so the card renders the
      // newly-picked language while the re-run is in flight.
      useAppStore.getState().setSessionLang(id, lang);
      redispatch(id, session.originalFile, lang);
    },
    [redispatch],
  );

  const reprocessWithRotation = useCallback(
    async (id: string, lang: string): Promise<void> => {
      // A rotate for this session is already awaiting its canvas pass —
      // ignore the click. Without this, two rapid clicks both rotate
      // the SAME source and land on +90° instead of +180°.
      if (rotatingRef.current.has(id)) return;
      const session = useAppStore.getState().sessions.find((s) => s.id === id);
      if (session === undefined) return;
      // Prefer the corrected JPEG — it's always present post-success and
      // always a plain JPEG, so canvas rotation works regardless of the
      // upload's source format (HEIC / PDF / etc.). Fall back to the
      // raw original if for some reason the corrected image is missing
      // (older session restored from IDB before correctedImage existed,
      // theoretically).
      const sourceBlob: Blob | null =
        session.correctedImage?.blob ?? session.originalFile;
      if (sourceBlob === null) return;
      rotatingRef.current.add(id);
      try {
        const rotated = await rotateBlob90Clockwise(sourceBlob);
        // The session may have been removed while the rotation ran —
        // don't enqueue an orphan.
        if (useAppStore.getState().sessions.every((s) => s.id !== id)) return;
        const rotatedFile = new File([rotated], session.filename, {
          type: rotated.type,
        });
        // Update originalFile FIRST so the next rotation reads the
        // already-rotated image. redispatch's resetForReprocess wipes it
        // from IDB if persistence is on; setResult will write the
        // post-success snapshot back.
        useAppStore.getState().setOriginalFile(id, rotatedFile);
        redispatch(id, rotatedFile, lang);
      } finally {
        rotatingRef.current.delete(id);
      }
    },
    [redispatch],
  );

  const cancel = useCallback((): void => {
    queueRef.current = [];
    inFlightByWorkerRef.current.clear();
    generationByIdRef.current.clear();
    rotatingRef.current.clear();
    idleWorkerIdxRef.current = [];
    inFlightFilesRef.current.clear();
    for (const worker of workersRef.current) worker.terminate();
    workersRef.current = [];
    useAppStore.getState().reset();
  }, []);

  const setDebugFolder = useCallback(
    (handle: FileSystemDirectoryHandle | null): void => {
      if (!DEBUG) return;
      debugFolderRef.current = handle;
    },
    [],
  );

  // The refs in the cleanup below intentionally read CURRENT state at
  // unmount (terminate every worker that's still alive, clear whatever
  // is still in flight). The "ref may have changed" warning is a
  // false positive for non-DOM refs holding workers + maps.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    return () => {
      queueRef.current = [];
      inFlightByWorkerRef.current.clear();
      generationByIdRef.current.clear();
      idleWorkerIdxRef.current = [];
      for (const worker of workersRef.current) worker.terminate();
      workersRef.current = [];
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  return {
    processFiles,
    cancel,
    setDebugFolder,
    reprocessWithManualQuad,
    confirmScan,
    reprocessWithRotation,
    reprocessWithLang,
  };
}
